import { useEffect } from "react";
import { Link } from "react-router-dom";
import { LOGO_URL } from "@/lib/api";
import { injectJsonLd } from "@/lib/jsonld";
import { ChevronRight, Loader2 } from "lucide-react";

export const Logo = ({ className = "", showName = true, testId = "procure-logo" }) => (
  <Link to="/" className={`flex shrink-0 items-center gap-2.5 ${className}`} data-testid={testId}>
    <img
      src={LOGO_URL}
      alt="Procure Group Ltd"
      width="446"
      height="130"
      className="h-10 w-auto shrink-0"
    />
    {showName && (
      <span className="hidden whitespace-nowrap border-l border-pg-border pl-2.5 text-[11px] leading-tight text-pg-muted lg:block">
        Procure
        <br />
        Group Ltd
      </span>
    )}
  </Link>
);

export const Shield = ({ className = "h-8 w-8" }) => (
  <svg viewBox="0 0 24 28" className={className} aria-hidden="true">
    <path
      d="M12 1 22 5v9c0 6-4.3 10.8-10 13C6.3 24.8 2 20 2 14V5l10-4Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M12 5.5 18.5 8v6c0 4-2.8 7.2-6.5 8.6" fill="currentColor" opacity="0.18" />
  </svg>
);

export const Container = ({ children, className = "", testId, "data-testid": dataTestId }) => (
  <div className={`pg-container ${className}`} data-testid={testId || dataTestId}>{children}</div>
);

export const Section = ({ children, pale = false, className = "", id, testId }) => (
  <section
    id={id}
    data-testid={testId}
    className={`py-16 md:py-24 ${pale ? "bg-pg-pale" : "bg-white"} ${className}`}
  >
    <Container>{children}</Container>
  </section>
);

export const Eyebrow = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-pg-primary mb-4">{children}</p>
);

export const SectionHeading = ({ eyebrow, title, intro, align = "left", className = "" }) => (
  <div className={`${align === "center" ? "mx-auto text-center max-w-2xl" : "max-w-3xl"} ${className}`}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-pg-dark">{title}</h2>
    {intro && <p className="mt-4 text-sm md:text-base text-pg-muted leading-relaxed">{intro}</p>}
  </div>
);

export const Bullets = ({ items, testId }) => (
  <ul className="space-y-2.5" data-testid={testId}>
    {(items || []).map((item, index) => (
      <li key={index} className="flex gap-3 text-sm text-pg-text leading-relaxed">
        <ChevronRight className="h-4 w-4 mt-0.5 shrink-0 text-pg-primary" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const SITE = "https://procuregroup.uk";
const DEFAULT_SHARE_IMAGE = `${SITE}/home-hero.webp`;

export const PageMeta = ({ title, description, canonical, robots, jsonLd, image }) => {
  useEffect(() => {
    if (title) document.title = title;
    const tag = (selector, create) => {
      let node = document.head.querySelector(selector);
      if (!node) {
        node = create();
        document.head.appendChild(node);
      }
      return node;
    };
    const named = (name, content) => {
      if (!content) return;
      tag(`meta[name="${name}"]`, () => {
        const node = document.createElement("meta");
        node.setAttribute("name", name);
        return node;
      }).setAttribute("content", content);
    };
    const property = (key, content) => {
      if (!content) return;
      tag(`meta[property="${key}"]`, () => {
        const node = document.createElement("meta");
        node.setAttribute("property", key);
        return node;
      }).setAttribute("content", content);
    };

    // One self-referencing canonical per page, without query strings or a trailing slash.
    const url = canonical
      || `${SITE}${window.location.pathname.replace(/\/+$/, "") || "/"}`;
    const share = image ? (image.startsWith("http") ? image : `${SITE}${image}`) : DEFAULT_SHARE_IMAGE;

    named("description", description);
    named("robots", robots || "index, follow");
    tag('link[rel="canonical"]', () => {
      const node = document.createElement("link");
      node.setAttribute("rel", "canonical");
      return node;
    }).setAttribute("href", url);

    property("og:type", "website");
    property("og:site_name", "Procure Group Ltd");
    property("og:locale", "en_GB");
    property("og:title", title);
    property("og:description", description);
    property("og:url", url);
    property("og:image", share);
    named("twitter:card", "summary_large_image");
    named("twitter:title", title);
    named("twitter:description", description);
    named("twitter:image", share);

    document.head.querySelectorAll('script[data-pg-jsonld="1"]').forEach((node) => node.remove());
    injectJsonLd(jsonLd, "data-pg-jsonld");
    // A link such as /about#qualifications must land on that section, not back at the top.
    const hash = window.location.hash.slice(1);
    const target = hash ? document.getElementById(hash) : null;
    if (target) target.scrollIntoView({ behavior: "instant", block: "start" });
    else window.scrollTo({ top: 0, behavior: "instant" });
  }, [title, description, canonical, robots, jsonLd, image]);
  return null;
};

export const Breadcrumbs = ({ items }) => (
  <nav className="flex flex-wrap items-center gap-1.5 text-xs text-pg-muted" aria-label="Breadcrumb">
    {items.map((item, index) => (
      <span key={index} className="flex items-center gap-1.5">
        {index > 0 && <span aria-hidden="true">/</span>}
        {item.to ? (
          <Link to={item.to} className="pg-link pg-tap">
            {item.label}
          </Link>
        ) : (
          <span className="text-pg-text">{item.label}</span>
        )}
      </span>
    ))}
  </nav>
);

export const Loading = ({ label = "Loading", testId = "loading-indicator" }) => (
  // The placeholder holds a viewport of height so the footer stays below the fold and nothing
  // visible moves when the content arrives. Layout shift is one of the measures Google reports on.
  <div className="min-h-[100vh] py-12" data-testid={testId}>
    <div className="mx-auto w-full max-w-5xl px-5">
      <div className="h-3 w-40 animate-pulse rounded bg-pg-border" />
      <div className="mt-6 h-9 w-3/4 animate-pulse rounded bg-pg-border" />
      <div className="mt-4 h-4 w-full animate-pulse rounded bg-pg-border/70" />
      <div className="mt-2 h-4 w-11/12 animate-pulse rounded bg-pg-border/70" />
      <div className="mt-2 h-4 w-2/3 animate-pulse rounded bg-pg-border/70" />
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {[0, 1, 2].map((index) => (
          <div key={index} className="h-28 animate-pulse rounded-xl bg-pg-border/60" />
        ))}
      </div>
      <p className="mt-10 flex items-center gap-2 text-sm text-pg-muted">
        <Loader2 className="h-4 w-4 animate-spin text-pg-primary" />
        {label}
      </p>
    </div>
  </div>
);

export const Notice = ({ children, tone = "info", testId }) => {
  const tones = {
    info: "bg-pg-pale border-pg-border text-pg-text",
    warn: "bg-[#FFF8E8] border-[#F0DFB4] text-[#5A4718]",
    error: "bg-[#FDF1F1] border-[#F0C9C9] text-[#7A2020]",
  };
  return (
    <div className={`rounded-lg border px-4 py-3 text-sm leading-relaxed ${tones[tone]}`} data-testid={testId}>
      {children}
    </div>
  );
};

export const StatusPill = ({ children, tone = "default" }) => {
  const tones = {
    default: "bg-pg-pale text-pg-dark border-pg-border",
    active: "bg-[#E7F1FE] text-pg-dark border-[#BBD8FA]",
    done: "bg-white text-pg-muted border-pg-border",
    warn: "bg-[#FFF8E8] text-[#5A4718] border-[#F0DFB4]",
    danger: "bg-[#FDECEC] text-[#7A1F1F] border-[#F3C4C4]",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${tones[tone] || tones.default}`}>
      {children}
    </span>
  );
};
